import { select, take } from "redux-saga/effects";
import { createSaga } from "../../../utils/saga";
import {
  getCachedControlFlowDSL,
  selectCachedControlFlowById,
  selectControlFlowPluginInfo,
} from "../control-flow/control-flow-selectors";
import { convertDSLToBackendBlocks } from "../control-flow/dsl-converters";
import { selectV2ApiById, selectV2ApiMetaById } from "../selectors";
import slice from "../slice";
import { persistV2ApiSaga } from "./persistV2Api";
import type { ApiDtoWithPb } from "../slice";
import type { PersistApiPayload } from "./persistV2Api";
import type { SagaActionMeta } from "../../../utils/saga";
import type * as BackendTypes from "../backend-types";
import type { PayloadActionWithMeta } from "store/utils/action";
import type { Action } from "store/utils/action";

function* hydrateBlockInternal(params: {
  blockId: string;
  apiId: string;
  updatingApiCallId: number;
  isEditingWithAi?: boolean;
}): Generator<unknown, undefined | BackendTypes.Api["blocks"], any> {
  const { blockId, apiId, isEditingWithAi } = params;

  let persistResult: ApiDtoWithPb | undefined;

  if (!isEditingWithAi) {
    yield take((action: Action) => {
      if (action.type === persistV2ApiSaga.success.type) {
        const { meta, payload } = action as PayloadActionWithMeta<
          ApiDtoWithPb,
          SagaActionMeta<PersistApiPayload>
        >;
        if (apiId === meta.args.api.metadata.id) {
          const persistUpdatingCallId = meta.args.updatingApiCallId;
          if (typeof persistUpdatingCallId === "number") {
            persistResult = payload;
            return persistUpdatingCallId >= params.updatingApiCallId;
          }
        }
      }
      return false;
    });

    if (!persistResult) {
      return;
    }
  }

  let api: ReturnType<typeof selectV2ApiById> | undefined;

  if (!isEditingWithAi) {
    api = persistResult;
  } else {
    api = yield select((state) => selectV2ApiById(state, apiId));
  }

  if (!api) {
    throw new Error(`API not found for id: ${apiId}`);
  }

  const pluginInfo: ReturnType<typeof selectControlFlowPluginInfo> =
    yield select(selectControlFlowPluginInfo);

  const currentControlFlow: ReturnType<typeof selectCachedControlFlowById> =
    yield select((state) => selectCachedControlFlowById(state, apiId));

  if (!currentControlFlow) {
    return;
  }
  const hydratedControlFlow = getCachedControlFlowDSL(api.apiPb, pluginInfo);
  const currentBlock = currentControlFlow.blocks[blockId];
  const hydratedBlock = hydratedControlFlow.blocks[blockId];
  if (!currentBlock || !hydratedBlock) {
    return;
  }
  const apiMeta: ReturnType<typeof selectV2ApiMetaById> = yield select(
    (state) => selectV2ApiMetaById(state, apiId),
  );
  // we hydrate other blocks too, since we can't guarantee that currentControlFlow isn't about to be changed by another hydrateBlock saga
  const hydratingBlocks = new Set(Object.keys(apiMeta?.hydratingBlocks ?? {}));
  hydratingBlocks.add(blockId);

  const blocks = Object.keys(currentControlFlow.blocks).reduce(
    (newBlocks: typeof currentControlFlow.blocks, blockId) => {
      if (hydratingBlocks.has(blockId)) {
        newBlocks[blockId] = hydratedControlFlow.blocks[blockId];
      } else {
        newBlocks[blockId] = currentControlFlow.blocks[blockId];
      }
      return newBlocks;
    },
    {},
  );

  const newControlFlow = {
    ...currentControlFlow,
    blocks,
  };
  const newBlocks = convertDSLToBackendBlocks(newControlFlow);
  return newBlocks;
}

export const hydrateBlockSaga = createSaga(
  hydrateBlockInternal,
  "hydrateBlockSaga",
  {
    sliceName: slice.name,
  },
);

slice.saga(hydrateBlockSaga, {
  start(state, { payload }) {
    const hydratingBlocks = state.meta[payload.apiId].hydratingBlocks ?? {};
    hydratingBlocks[payload.blockId] = true;
    state.meta[payload.apiId].hydratingBlocks = hydratingBlocks;
  },
  success(state, { payload, meta }) {
    const hydratingBlocks = state.meta[meta.args.apiId].hydratingBlocks ?? {};
    delete hydratingBlocks[meta.args.blockId];

    if (!payload || !state.entities[meta.args.apiId]?.apiPb) {
      return;
    }
    state.entities[meta.args.apiId] = {
      ...state.entities[meta.args.apiId],
      apiPb: {
        ...state.entities[meta.args.apiId].apiPb,
        blocks: payload,
      },
    };
  },
  error(state, { payload, meta }) {
    const hydratingBlocks = state.meta[meta.args.apiId].hydratingBlocks ?? {};
    delete hydratingBlocks[meta.args.blockId];
  },
});
